import { useState } from 'react'
import { useProdottiAttivi, useCategorieAttive } from '@/hooks/useProdotti'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Search, ShoppingBag, Check } from 'lucide-react'

/**
 * Componente per selezionare prodotti singoli.
 * Mostra card con nome, prezzo, categoria e giacenza.
 * 
 * Props:
 * - selectedItems: array di { prodotto, quantita }
 * - onSelect: callback quando si seleziona un prodotto
 * - onUpdateQuantity: callback per aggiornare quantità
 * - onRemove: callback per rimuovere un prodotto
 */
export default function ProdottoSelector({ 
  selectedItems = [], 
  onSelect, 
  onUpdateQuantity, 
  onRemove,
  filterVendibileSingolarmente = true,
}) {
  const [search, setSearch] = useState('')
  const [categoriaFilter, setCategoriaFilter] = useState('')
  
  const { data: prodottiData, isLoading } = useProdottiAttivi()
  const { data: categorieData } = useCategorieAttive()
  
  const prodotti = prodottiData?.results || prodottiData || []
  const categorie = categorieData?.results || categorieData || []
  
  const filteredProdotti = prodotti.filter(p => {
    const matchSearch = 
      p.nome?.toLowerCase().includes(search.toLowerCase()) ||
      p.codice?.toLowerCase().includes(search.toLowerCase())
    const matchCategoria = !categoriaFilter || p.categoria === parseInt(categoriaFilter)
    const matchVendibile = !filterVendibileSingolarmente || p.vendibile_singolarmente === true
    return matchSearch && matchCategoria && matchVendibile
  })

  const isSelected = (id) => selectedItems.some(item => item.prodotto === id)
  const getSelectedItem = (id) => selectedItems.find(item => item.prodotto === id)

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0)
  }

  const handleSelect = (prodotto) => {
    if (isSelected(prodotto.id)) {
      onRemove?.(prodotto.id)
    } else {
      onSelect?.({
        prodotto: prodotto.id,
        prodotto_data: prodotto,
        quantita: 1,
        prezzo_unitario_cents: prodotto.prezzo_vendita_cents,
      })
    }
  }
  
  const handleUpdateQuantity = (prodottoId, newQuantity) => {
    const item = getSelectedItem(prodottoId)
    if (!item) return
    
    const quantitaValidata = Math.max(1, newQuantity)
    onUpdateQuantity?.(prodottoId, quantitaValidata)
  }

  return (
    <div className="space-y-4">
      {/* Filtri */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca prodotto per nome o codice..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={categoriaFilter}
          onChange={(e) => setCategoriaFilter(e.target.value)}
          className="border rounded-md px-3 min-w-[150px]"
        >
          <option value="">Tutte le categorie</option>
          {categorie.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.nome}</option>
          ))}
        </select>
      </div>

      {/* Riepilogo selezionati */}
      {selectedItems.length > 0 && (
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm font-medium mb-2">
            Prodotti selezionati: {selectedItems.length}
          </p>
          <div className="overflow-hidden rounded-md border border-border divide-y divide-border">
            {selectedItems.map((item) => (
              <div
                key={item.prodotto}
                className="flex flex-col gap-2 px-3 py-2 text-sm odd:bg-background even:bg-muted/40 focus-within:bg-primary/10 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary/50 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="min-w-0 flex-1 font-medium">
                  {item.prodotto_data?.nome || `Prodotto #${item.prodotto}`}
                </span>
                <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 w-6 p-0"
                    onClick={() => handleUpdateQuantity(item.prodotto, item.quantita - 1)}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    aria-label={`Quantità di ${item.prodotto_data?.nome || `Prodotto #${item.prodotto}`}`}
                    min="1"
                    value={item.quantita}
                    onChange={(e) => handleUpdateQuantity(item.prodotto, parseInt(e.target.value) || 1)}
                    className="w-16 h-6 text-center p-0"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 w-6 p-0"
                    onClick={() => handleUpdateQuantity(item.prodotto, item.quantita + 1)}
                  >
                    +
                  </Button>
                  <span className="ml-2 w-24 text-right tabular-nums text-muted-foreground">
                    {formatCurrency((item.prezzo_unitario_cents / 100) * item.quantita)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista prodotti */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Caricamento prodotti...
        </div>
      ) : filteredProdotti.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>{search || categoriaFilter ? 'Nessun prodotto trovato' : 'Nessun prodotto disponibile'}</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 max-h-96 overflow-y-auto">
          {filteredProdotti.map((prodotto) => {
            const selected = isSelected(prodotto.id)
            const giacenza = prodotto.giacenza_disponibile ?? prodotto.giacenza ?? 'N/D'
            
            return (
              <div
                key={prodotto.id}
                onClick={() => handleSelect(prodotto)}
                className={`
                  p-3 border rounded-lg transition-all cursor-pointer
                  ${selected 
                    ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                    : 'hover:bg-accent hover:border-accent-foreground/20'
                  }
                `}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{prodotto.nome}</p>
                    <p className="text-xs text-muted-foreground font-mono">{prodotto.codice}</p>
                  </div>
                  {selected && (
                    <div className="bg-primary text-primary-foreground rounded-full p-1 ml-2">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between items-end">
                  <div className="space-y-1">
                    {prodotto.categoria_nome && (
                      <Badge variant="outline" className="text-xs">
                        {prodotto.categoria_nome}
                      </Badge>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Disp: <span className={giacenza > 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
                        {giacenza}
                      </span>
                    </p>
                  </div>
                  <p className="font-bold">
                    {formatCurrency(prodotto.prezzo_vendita_cents / 100)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
